import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Stack } from '@mui/material';
import { DisplayResultsBy, TestResultFormField, OpenLogResultModal, Room, TestResultForm, WaterlineTestResult, TestResultFormErrors } from '../types';
import CloseModalButton from '../../../../components/CloseModalButton';
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from '../../../../components/Dialog.styled';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAppSelector } from '../../../../redux/store';
import { selectCurrentCustomerNumber } from '../../../../redux/customer';
import { addError } from '../../../../redux/alerts';
import { useAppDispatch } from '../../../../redux/store';
import {
  addInOfficeWaterlineTestResult as createTestResult,
  updateInOfficeWaterlineTestResult as updateTestResult,
} from '../../../../services/waterlineService';
import { formatDateToYYYYMMDD } from '../../../../helpers/date';
import useTeamMembers from '../../../../hooks/useTeamMembers';
import { getTestResultFormData } from '../utils';
import TestField from './result-modal/TestField';
import RoomField from './result-modal/RoomField';
import TreatmentMethodField from './result-modal/TreatmentMethodField';
import TestTypeField from './result-modal/TestTypeField';
import TeamMemberField from './result-modal/TeamMemberField';
import TestDateField from './result-modal/TestDateField';
import SampleNumberField from './result-modal/SampleNumberField';
import PHField from './result-modal/PHField';
import TDSField from './result-modal/TDSField';
import CFUField from './result-modal/CFUField';
import ResultDateField from './result-modal/ResultDateField';

dayjs.extend(utc);

const today = new Date()

const initialFormDataState: TestResultForm = {
  retest: null,
  room_id: null,
  result_description: null,
  product: null,
  hpc_method: null,
  tested_by: null,
  test_date: today.toISOString(),
  sample_number: null,
  pH: null,
  TDS: null,
  cfu: null,
  report_date: today.toISOString(),
}

type LogInOfficeResultModal = {
  open: boolean,
  setOpenModal: Dispatch<SetStateAction<OpenLogResultModal>>,
  rooms: Room[],
  displayBy: DisplayResultsBy,
  testResult: WaterlineTestResult | null,
  getCompliance: () => void,
  getTestResults: () => void,
}

const LogInOfficeResultModal = (props: LogInOfficeResultModal) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState(initialFormDataState);
  const [formErrors, setFormErrors] = useState<TestResultFormErrors>({ pH: false, TDS: false, sample_number: false });
  const [loading, setLoading] = useState(false);
  const teamMembers = useTeamMembers()
  const customerNumber = useAppSelector(selectCurrentCustomerNumber);

  const {
    open,
    setOpenModal,
    rooms,
    displayBy,
    testResult,
    getCompliance,
    getTestResults,
  } = props

  useEffect(() => {
    if (testResult) {
      const nextFormData = getTestResultFormData(formData, testResult)
      
      setFormData(nextFormData)
    }
  }, [testResult])

  const requiredFields = new Set<TestResultFormField>([
    'retest',
    'room_id',
    'product',
    'hpc_method',
    'tested_by',
    'test_date',
    'cfu',
    'report_date'
  ])

  const handleClose = () => {
    setOpenModal({ open: false, testResult: null })
  }

  const disableSubmit = useMemo(() => {
    const isMissingRequired = Object.entries(formData).some(entry => {
      const field = entry[0] as TestResultFormField
      const value = entry[1]

      const isRequired = requiredFields.has(field)
      const hasValue = field === 'retest' ? value !== null : Boolean(value)

      return isRequired && hasValue === false
    })

    const hasErrors = Object.values(formErrors).some(error => error)

    return isMissingRequired || hasErrors
  }, [formData, formErrors])

  const submitForm = async () => {
    try {
      const fields = Object.keys(formData) as TestResultFormField[]

      fields.forEach(field => {
        const hasValue = Boolean(formData[field])

        if (hasValue === false && field !== 'retest') {
          delete formData[field]
        }
      })

      const isPass = Number(formData['cfu']) < 500;
      const inOfficeTest = true;

      const payload = {
        ...formData,
        test_date: formatDateToYYYYMMDD(formData.test_date),
        report_date: formatDateToYYYYMMDD(formData.report_date),
        retest: formData.retest ?? false,
        is_pass: isPass,
        in_office_test: inOfficeTest,
        customer_number: customerNumber,
        ...(testResult ? { test_result_id: testResult.test_result_id } : {})
      }

      setLoading(true)

      testResult
        ? await updateTestResult(customerNumber, payload)
        : await createTestResult(customerNumber, payload)

      setLoading(false);
      setFormData(initialFormDataState);
      getTestResults();
      getCompliance();
      handleClose();
    } catch (err) { 
      console.error(err);
      setLoading(false);
      dispatch(addError(`There was an error ${testResult ? 'updating' : 'creating'} In-Office Result.`));
      setFormData(initialFormDataState);
      handleClose();
    }
  }

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>{testResult ? 'Edit' : 'Log'} In-Office Result</StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={'16px'} marginTop={'16px'} marginBottom={'8px'}>
          <TestField
            formData={formData}
            setFormData={setFormData}
          />
          <RoomField
            rooms={rooms}
            formData={formData}
            setFormData={setFormData}
          />
          <TreatmentMethodField
            formData={formData}
            setFormData={setFormData}
          />
          <TestTypeField
            formData={formData}
            setFormData={setFormData}
          />
          <TeamMemberField
            teamMembers={teamMembers}
            formData={formData}
            setFormData={setFormData}
          />
          <TestDateField
            testResult={testResult}
            displayBy={displayBy}
            formData={formData}
            setFormData={setFormData}
          />
          <SampleNumberField
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
          <PHField
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
          <TDSField
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
          <CFUField
            formData={formData}
            setFormData={setFormData}
          />
          <ResultDateField
            formData={formData}
            setFormData={setFormData}
          />
        </Stack>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={submitForm} disabled={disableSubmit || loading}>Save</Button>
      </StyledDialogFooter>
    </Dialog>
  )
}

export default LogInOfficeResultModal
